import { useMemo, useState } from 'react';
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  type PaginationState,
} from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, Filter, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DataTablePagination, ErrorAlert, PageHeader, RowDensitySwitch } from '@/components/core';
import { useGetLeaveCategoriesLookup } from '@/modules/time-off/my-time-off/hooks/use-leave';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui-kit/toggle-group';
import { TABLE_CARD_CLASS } from '@/components/core/advance-data-table/advance-data-table';
import { cn } from '@/lib/utils';
import { orgWallClock } from '@/lib/utils/custom-date/custom-date';
import { useIsMobile } from '@/hooks/use-media-query';
import { usePersonPresence } from '@/modules/hr-directory';
import { useInitialPageSize } from '@/hooks/use-server-table';
import { LeaveCalendarFilters } from '../../components/leave-calendar-filters/leave-calendar-filters';
import { LeaveCalendarPeriod } from '../../components/leave-calendar-period/leave-calendar-period';
import { useDepartmentScope, useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { LeaveCalendarFilterDialog } from '../../components/leave-calendar-filter-dialog/leave-calendar-filter-dialog';
import {
  LeaveCalendarAgenda,
  agendaLeaveCount,
} from '../../components/leave-calendar-agenda/leave-calendar-agenda';
import { LeaveCalendarGrid } from '../../components/leave-calendar-grid/leave-calendar-grid';
import { LeaveCalendarMobile } from '../../components/leave-calendar-mobile/leave-calendar-mobile';
import { hrTermOn } from '@/lib/utils/work-days/work-days';
import { scheduledWorkSecondsOn } from '@/modules/time-attendance/attendance/services/attendance-rules';
import { useGetHrTermsOfUsers, useGetLeaveCalendarData } from '../../hooks/use-leave-calendar';
import {
  LeaveCalendarEmployeeGroup,
  LeaveCalendarLeaveEvent,
} from '../../types/leave-calendar.types';
import { LeaveDetailsDialog } from '../../components/leave-details-dialog/leave-details-dialog';
import {
  addDays,
  addMonthsKeepingDay,
  formatDayLabel,
  monthDaysOf,
  monthStartOf,
  weekDaysFrom,
  weekStartOf,
} from '../../utils/leave-calendar-week';

type CalendarView = 'week' | 'month' | 'agenda';

const FIRST_PAGE: PaginationState = { pageIndex: 0, pageSize: 15 };
const NO_EMPLOYEES: LeaveCalendarEmployeeGroup[] = [];

/** Today ("YYYY-MM-DD") in the organization's time zone, never the device's. */
const orgToday = () => {
  const { year, month, day } = orgWallClock(new Date());
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/** The days a view shows around `day`: its Monday-to-Sunday week, or its whole month. */
const daysOf = (view: CalendarView, day: string) =>
  view === 'week' ? weekDaysFrom(weekStartOf(day)) : monthDaysOf(day);

export const LeaveCalendarPage = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const today = useMemo(orgToday, []);
  const [view, setView] = useState<CalendarView>('week');
  // Any day of the period shown; the view turns it into its week or month. On a phone it is also the
  // day whose leave is listed, so it survives switching views or resizing.
  const [anchor, setAnchor] = useState(today);
  const [search, setSearch] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  // Prototype filters: leave status and leave category; empty means every one.
  const [status, setStatus] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // The leave whose details are open (a click on its bar).
  const [opened, setOpened] = useState<
    { leave: LeaveCalendarLeaveEvent; employee: LeaveCalendarEmployeeGroup } | undefined
  >();
  const { data: categoriesData } = useGetLeaveCategoriesLookup();
  const categories =
    (categoriesData as { LeaveCategorys?: { items: { ItemId: string; Title: string }[] } })
      ?.LeaveCategorys?.items ?? [];
  const initialPageSize = useInitialPageSize(FIRST_PAGE.pageSize);
  const [pagination, setPagination] = useState<PaginationState>({
    ...FIRST_PAGE,
    pageSize: initialPageSize,
  });

  const days = useMemo(() => daysOf(view, anchor), [view, anchor]);
  const firstDay = days[0];
  const lastDay = days[days.length - 1];
  // The department scope (the Department permissions, as the Leave Manager) always applies, so a head
  // of department sees only their departments' leave; the picker only narrows within it.
  const { getDepartmentIdCondition, isLoading: isScopeLoading } = useDepartmentScope();
  const { data, isLoading, error } = useGetLeaveCalendarData({
    startDate: firstDay,
    endDate: lastDay,
    // Every status unless one is picked: pending and rejected leave show beside the approved (prototype).
    status: status || undefined,
    leaveCategoryId: categoryId || undefined,
    departmentId: getDepartmentIdCondition(departmentId),
  });

  const toFirstPage = () => setPagination((current) => ({ ...current, pageIndex: 0 }));
  const goTo = (day: string) => {
    setAnchor(day);
    toFirstPage();
  };
  // Moves by whole periods from the anchor, keeping its weekday (week) or day of the month (month),
  // and lands on today when the new period holds it.
  const step = (direction: 1 | -1) => {
    const next =
      view === 'week' ? addDays(anchor, 7 * direction) : addMonthsKeepingDay(anchor, direction);
    goTo(daysOf(view, next).includes(today) ? today : next);
  };

  const periodLabel =
    view !== 'week'
      ? formatDayLabel(firstDay, i18n.language, { month: 'long', year: 'numeric' })
      : `${formatDayLabel(firstDay, i18n.language, { day: 'numeric', month: 'short' })} - ${formatDayLabel(lastDay, i18n.language, { day: 'numeric', month: 'short', year: 'numeric' })}`;

  // Every employee on leave in the period is loaded at once, so the page searches and pages them itself.
  const employees = data?.employees ?? NO_EMPLOYEES;
  const matching = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return keyword
      ? employees.filter((employee) => employee.DisplayName.toLowerCase().includes(keyword))
      : employees;
  }, [employees, search]);

  // The phone view lists who is away, so it keeps to approved leave.
  const mobileEmployees = useMemo(
    () =>
      matching
        .map((employee) => ({
          ...employee,
          LeaveEvents: employee.LeaveEvents.filter((leave) => leave.Status === 'Approved'),
        }))
        .filter((employee) => employee.LeaveEvents.length > 0),
    [matching]
  );

  const table = useReactTable({
    data: matching,
    columns: [],
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
  });
  const pageEmployees = table.getRowModel().rows.map((row) => row.original);

  // A group's ItemId is the employee's IAM user id. The phone view looks up only the employee it opens.
  // The Leaves view lists everyone at once; the grid, its page.
  const listedEmployees = view === 'agenda' ? matching : pageEmployees;
  const listedIds = isMobile ? [] : listedEmployees.map((employee) => employee.ItemId);
  const { byUserId } = useEmployeesByUserIds(listedIds);
  // Under a name: the department (prototype `unit`), else the email. The Leaves view adds the designation.
  const subtitleOf = (userId: string) => {
    const employee = byUserId.get(userId);
    return employee?.DepartmentName || employee?.Email;
  };
  const agendaSubtitleOf = (userId: string) => {
    const employee = byUserId.get(userId);
    const text = [employee?.Designation, employee?.DepartmentName].filter(Boolean).join(' · ');
    return text || employee?.Email;
  };

  // Each person's own weekend: a day their HR term in force schedules no hours on. Without a term or its
  // work days nothing is guessed (as the attendance days).
  const { data: hrTerms } = useGetHrTermsOfUsers(listedIds);
  // Today's presence for the avatars, as People shows it (only for viewers who may read it).
  const { presenceOf } = usePersonPresence(listedIds);
  const weekendOf = (userId: string, day: string) => {
    const term = hrTermOn(hrTerms?.[userId], day);
    return Boolean(term?.WorkDays?.length) && scheduledWorkSecondsOn(term?.WorkDays, day) <= 0;
  };

  const changeView = (next: string) => {
    setView(next as CalendarView);
    toFirstPage();
  };
  const changeDepartment = (next: string) => {
    setDepartmentId(next);
    toFirstPage();
  };
  const changeStatus = (next: string) => {
    setStatus(next);
    toFirstPage();
  };
  const changeCategory = (next: string) => {
    setCategoryId(next);
    toFirstPage();
  };
  const changeSearch = (next: string) => {
    setSearch(next);
    toFirstPage();
  };

  const viewTabs = (
    <Tabs value={view} onValueChange={changeView}>
      <TabsList>
        <TabsTrigger value="week">{t('LEAVE_CALENDAR_WEEK')}</TabsTrigger>
        <TabsTrigger value="month">{t('LEAVE_CALENDAR_MONTH')}</TabsTrigger>
      </TabsList>
    </Tabs>
  );
  const todayButton = (
    <Button variant="outline" onClick={() => goTo(today)}>
      {t('TODAY')}
    </Button>
  );
  const stepButton = (direction: 1 | -1) => (
    <Button
      variant="outline"
      size="icon"
      className="shrink-0"
      aria-label={t(
        direction === 1
          ? view === 'week'
            ? 'LEAVE_CALENDAR_NEXT_WEEK'
            : 'LEAVE_CALENDAR_NEXT_MONTH'
          : view === 'week'
            ? 'LEAVE_CALENDAR_PREVIOUS_WEEK'
            : 'LEAVE_CALENDAR_PREVIOUS_MONTH'
      )}
      onClick={() => step(direction)}
    >
      {direction === 1 ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
    </Button>
  );
  const isLoadingData = isLoading || isScopeLoading;
  // The search and the picked department; a department locked by the scope is not counted.
  const activeFilters = Number(search.trim() !== '') + Number(departmentId !== '');

  const mobileToolbar = (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        {stepButton(-1)}
        <span className="min-w-0 flex-1 truncate text-center text-sm font-medium">
          {periodLabel}
        </span>
        {stepButton(1)}
      </div>
      <div className="flex items-center gap-2">
        {viewTabs}
        <div className="ml-auto flex items-center gap-2">
          {todayButton}
          <Button
            type="button"
            variant="outline"
            size="icon"
            // As tall as the buttons beside it, like the card tables' filter button.
            className="relative size-9 shrink-0"
            aria-label={t('FILTER')}
            onClick={() => setIsFilterOpen(true)}
          >
            <Filter className="size-5" fill={activeFilters > 0 ? 'currentColor' : 'none'} />
            {activeFilters > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs text-primary-foreground">
                {activeFilters}
              </span>
            )}
          </Button>
        </div>
      </div>

      <LeaveCalendarFilterDialog
        open={isFilterOpen}
        onOpenChange={setIsFilterOpen}
        applied={{ day: anchor, departmentId, search }}
        onApply={(filters) => {
          setAnchor(filters.day);
          setDepartmentId(filters.departmentId);
          setSearch(filters.search);
          toFirstPage();
        }}
      />
    </div>
  );

  // Prototype `.tcal`: the search and filters lead, the period controls and the view switch follow
  // on the right, all inside the table card.
  const viewSwitch = (
    <ToggleGroup
      type="single"
      variant="outline"
      value={view}
      onValueChange={(next) => next && changeView(next)}
      aria-label={t('LEAVE_CALENDAR')}
    >
      <ToggleGroupItem value="week" className="px-2.5 text-[13px]">
        {t('LEAVE_CALENDAR_WEEK')}
      </ToggleGroupItem>
      <ToggleGroupItem value="month" className="px-2.5 text-[13px]">
        {t('LEAVE_CALENDAR_MONTH')}
      </ToggleGroupItem>
      <ToggleGroupItem value="agenda" className="px-2.5 text-[13px]">
        {t('LEAVE_CALENDAR_LEAVES')}
      </ToggleGroupItem>
    </ToggleGroup>
  );
  const isCurrentPeriod = days.includes(today);

  const desktopToolbar = (
    <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
      <div className="relative w-64">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          className="pl-8"
          placeholder={t('SEARCH_BY_NAME')}
          aria-label={t('SEARCH_BY_NAME')}
          value={search}
          onChange={(event) => changeSearch(event.target.value)}
        />
      </div>
      <LeaveCalendarFilters
        departmentId={departmentId}
        onDepartmentChange={changeDepartment}
        categoryId={categoryId}
        onCategoryChange={changeCategory}
        categories={categories}
        status={status}
        onStatusChange={changeStatus}
      />
      <span className="flex-1" />
      <LeaveCalendarPeriod
        view={view === 'week' ? 'week' : 'month'}
        firstDay={view === 'week' ? firstDay : monthStartOf(firstDay)}
        label={periodLabel}
        isCurrentPeriod={isCurrentPeriod}
        today={today}
        locale={i18n.language}
        onStep={step}
        onPick={goTo}
        onToday={() => goTo(today)}
      />
      {viewSwitch}
    </div>
  );

  const swatch = 'inline-block size-3 shrink-0 rounded-sm border';
  const legend = (
    <ul className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-xs text-muted-foreground">
      <li className="inline-flex items-center gap-1.5">
        <i className={cn(swatch, 'border-tint-blue-border bg-accent')} aria-hidden />
        {t('APPROVED')}
      </li>
      <li className="inline-flex items-center gap-1.5">
        <i className={cn(swatch, 'border-warning-border bg-warning-subtle')} aria-hidden />
        {t('PENDING')}
      </li>
      <li className="inline-flex items-center gap-1.5">
        <i className={cn(swatch, 'border-destructive-border bg-destructive-subtle')} aria-hidden />
        {t('REJECTED')}
      </li>
      {view !== 'agenda' && (
        <li className="inline-flex items-center gap-1.5">
          <i
            className={cn(
              swatch,
              'border-border bg-background dark:bg-[color-mix(in_oklch,oklch(var(--surface-subtle))_55%,oklch(var(--card)))]'
            )}
            aria-hidden
          />
          {t('LEAVE_CALENDAR_WEEKEND_LEGEND')}
        </li>
      )}
    </ul>
  );

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Request Time Off and Request TOIL are on My Time Off only (2 Nov 2026). */}
      <PageHeader title={t('CALENDAR')} />

      {error ? (
        <ErrorAlert isError message={error instanceof Error ? error.message : String(error)} />
      ) : isMobile ? (
        <>
          {mobileToolbar}
          <LeaveCalendarMobile
            days={days}
            selectedDay={anchor}
            today={today}
            employees={mobileEmployees}
            holidays={data?.holidays ?? []}
            isLoading={isLoadingData}
            onSelectDay={goTo}
            onStep={step}
          />
        </>
      ) : (
        // One table card (prototype `.tcard.tcal`): toolbar, grid and footer with the legend and the pager.
        <div className={TABLE_CARD_CLASS}>
          {desktopToolbar}
          {view === 'agenda' ? (
            <LeaveCalendarAgenda
              days={days}
              today={today}
              locale={i18n.language}
              employees={matching}
              // Until the departments load, a scoped user's scope matches nothing; show the loader.
              isLoading={isLoadingData}
              emptyMessage={t('LEAVE_CALENDAR_EMPTY_AGENDA', { month: periodLabel })}
              subtitleOf={agendaSubtitleOf}
              weekendOf={weekendOf}
              presenceOf={presenceOf}
              onOpenLeave={(leave, employee) => setOpened({ leave, employee })}
            />
          ) : (
            <LeaveCalendarGrid
              days={days}
              today={today}
              employees={pageEmployees}
              holidays={data?.holidays ?? []}
              // Until the departments load, a scoped user's scope matches nothing; show the loader.
              isLoading={isLoadingData}
              emptyMessage={t(
                view === 'week' ? 'LEAVE_CALENDAR_EMPTY' : 'LEAVE_CALENDAR_EMPTY_MONTH'
              )}
              subtitleOf={subtitleOf}
              weekendOf={weekendOf}
              presenceOf={presenceOf}
              onOpenLeave={(leave, employee) => setOpened({ leave, employee })}
            />
          )}
          <div className="-mt-px flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t px-4 py-2.5 max-md:px-3">
            {legend}
            {view === 'agenda' ? (
              // The row-height choice every table offers, beside the count.
              <div className="ml-auto flex flex-wrap items-center gap-4">
                <span className="text-[13px] tabular-nums text-muted-foreground">
                  {t('LEAVE_CALENDAR_LEAVE_COUNT', {
                    count: agendaLeaveCount(matching, days),
                    month: periodLabel,
                  })}
                </span>
                <RowDensitySwitch />
              </div>
            ) : matching.length > 0 ? (
              <div className="min-w-[16rem] flex-1">
                <DataTablePagination
                  table={table}
                  showSelectedRowContent={false}
                  totalCount={matching.length}
                />
              </div>
            ) : (
              <div className="ml-auto">
                <RowDensitySwitch />
              </div>
            )}
          </div>
        </div>
      )}

      <LeaveDetailsDialog
        entry={opened}
        email={opened && (subtitleOf(opened.employee.ItemId) ?? opened.employee.Email)}
        presenceOf={presenceOf}
        onClose={() => setOpened(undefined)}
      />
    </div>
  );
};
